import * as React from "react"
import { AnimatePresence, motion } from "motion/react"
import { CaretRight } from "@phosphor-icons/react"
import { z } from "zod"

import { cn } from "~/utils/helpers"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"

const expandedSchema = z.boolean()

type ExpandableCardContextValue = {
  isExpanded: boolean
  toggle: () => void
}

const ExpandableCardContext = React.createContext<ExpandableCardContextValue | null>(null)

const useExpandableCardContext = () => {
  const ctx = React.useContext(ExpandableCardContext)
  if (!ctx) {
    throw new Error(
      "ExpandableCardHeader / ExpandableCardContent must be rendered inside <ExpandableCard>",
    )
  }
  return ctx
}

type ExpandableCardProps = React.HTMLAttributes<HTMLDivElement> & {
  defaultExpanded?: boolean
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  /**
   * When set (and the card is uncontrolled), the expand/collapse state is persisted
   * to org-scoped localStorage under this key, so it survives remounts and tab
   * switches. Keys must be stable and unique per card-instance — for inspector cards
   * use {@link useInspectorExpandKey} so the key is scoped per route automatically.
   */
  persistKey?: string
}

const ExpandableCardBase = React.forwardRef<
  HTMLDivElement,
  Omit<ExpandableCardProps, "persistKey">
>(({ className, defaultExpanded = false, expanded, onExpandedChange, children, ...props }, ref) => {
  const isControlled = expanded !== undefined
  const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(defaultExpanded)
  const isExpanded = isControlled ? expanded : uncontrolledExpanded

  const toggle = React.useCallback(() => {
    const next = !isExpanded
    if (!isControlled) setUncontrolledExpanded(next)
    onExpandedChange?.(next)
  }, [isExpanded, isControlled, onExpandedChange])

  const ctxValue = React.useMemo<ExpandableCardContextValue>(
    () => ({ isExpanded, toggle }),
    [isExpanded, toggle],
  )

  return (
    <ExpandableCardContext.Provider value={ctxValue}>
      <div
        ref={ref}
        className={cn(
          "rounded-[12px] bg-component text-fg-base shadow-card-rest overflow-hidden",
          // The header is transparent and the shell paints its hover, so the strip behind the content
          // panel's rounded corners matches it. A layer over bg-component, not a swap: the dark token is translucent.
          "has-[>[data-slot=expandable-card-header]:hover]:[background-image:linear-gradient(var(--theme-background-component-hover),var(--theme-background-component-hover))]",
          "has-[>[data-slot=expandable-card-header]:focus-visible]:[background-image:linear-gradient(var(--theme-background-component-hover),var(--theme-background-component-hover))]",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </ExpandableCardContext.Provider>
  )
})
ExpandableCardBase.displayName = "ExpandableCardBase"

// Persisted, uncontrolled variant: drives ExpandableCardBase as controlled, backing the
// expanded state with org-scoped localStorage. Mounted with key={persistKey} by the
// wrapper so a changed key re-reads storage (useSafeLocalStorage doesn't re-read on key
// change mid-mount).
const PersistentExpandableCard = React.forwardRef<
  HTMLDivElement,
  Omit<ExpandableCardProps, "persistKey"> & { persistKey: string }
>(({ persistKey, defaultExpanded = false, onExpandedChange, ...props }, ref) => {
  const [expanded, setExpanded] = useSafeLocalStorage(persistKey, defaultExpanded, expandedSchema)
  return (
    <ExpandableCardBase
      ref={ref}
      {...props}
      expanded={expanded}
      onExpandedChange={(next) => {
        setExpanded(next)
        onExpandedChange?.(next)
      }}
    />
  )
})
PersistentExpandableCard.displayName = "PersistentExpandableCard"

/**
 * Collapsible card: clickable header with a rotating caret over an animated body. Collapsed by default.
 * Canonical intent: {@link ./intents.ts EXPANDABLE_CARD_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const ExpandableCard = React.forwardRef<HTMLDivElement, ExpandableCardProps>(
  ({ persistKey, ...props }, ref) => {
    // Persist only when given a key AND uncontrolled — controlled callers own the state.
    if (persistKey !== undefined && props.expanded === undefined) {
      return (
        <PersistentExpandableCard key={persistKey} ref={ref} persistKey={persistKey} {...props} />
      )
    }
    return <ExpandableCardBase ref={ref} {...props} />
  },
)
ExpandableCard.displayName = "ExpandableCard"

const INTERACTIVE_TARGET_SELECTOR =
  'button, a, input, select, textarea, label, [role="button"], [role="checkbox"], [role="switch"], [role="menuitem"], [role="tab"], [role="link"], [contenteditable="true"]'

/**
 * Props that turn an element into the card's expand/collapse toggle, plus the current state. Shared by
 * {@link ExpandableCardHeader} and `InspectorCardHeader` so the interactive-target guard has one implementation.
 *
 * The guard must exclude `currentTarget`: the header carries `role="button"`, so a bare
 * `closest(INTERACTIVE_TARGET_SELECTOR)` matches it from any child and swallows every click on the title or
 * caret, leaving the card toggling only on its bare padding.
 */
const useExpandableCardToggle = ({
  onClick,
  onKeyDown,
}: {
  onClick?: React.MouseEventHandler<HTMLElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>
} = {}) => {
  const { isExpanded, toggle } = useExpandableCardContext()

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e)
    if (e.defaultPrevented) return
    const target = e.target as HTMLElement | null
    // Portaled descendants (Radix Dialog/Popover) bubble through React's tree but
    // aren't DOM descendants of the header. Ignore those clicks.
    if (!target || !e.currentTarget.contains(target)) return
    if (target !== e.currentTarget) {
      const interactiveAncestor = target.closest(INTERACTIVE_TARGET_SELECTOR)
      if (interactiveAncestor && interactiveAncestor !== e.currentTarget) return
    }
    toggle()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(e)
    if (e.defaultPrevented) return
    if (e.target !== e.currentTarget) return
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      toggle()
    }
  }

  return {
    isExpanded,
    toggleProps: {
      role: "button",
      tabIndex: 0,
      "data-slot": "expandable-card-header",
      "aria-expanded": isExpanded,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
    },
  } as const
}

const ExpandableCardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, onClick, onKeyDown, ...props }, ref) => {
    const { isExpanded, toggleProps } = useExpandableCardToggle({ onClick, onKeyDown })

    return (
      <div
        ref={ref}
        {...toggleProps}
        className={cn(
          "flex flex-row items-center gap-3 py-3 px-4 h-11 cursor-pointer select-none focus-visible:outline-none",
          className,
        )}
        {...props}
      >
        <motion.div
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30,
            mass: 0.6,
          }}
          className="flex-shrink-0"
        >
          <CaretRight size={16} className="text-fg-muted" />
        </motion.div>
        {/* Caret is a sibling, so children share the row with it: `min-w-0 flex-1` keeps a
            `w-full` child (several headers wrap their content in one) inside the header. */}
        <div className="flex min-w-0 flex-1 items-center gap-3">{children}</div>
      </div>
    )
  },
)
ExpandableCardHeader.displayName = "ExpandableCardHeader"

const ExpandableCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    fill?: boolean
  }
>(({ className, children, fill = false }, ref) => {
  const { isExpanded } = useExpandableCardContext()

  if (fill) {
    return (
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            ref={ref}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } }}
            className={cn(
              "px-4 py-3 bg-base flex-1 min-h-0 overflow-y-auto rounded-t-[12px] shadow-card-rest",
              className,
            )}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          ref={ref}
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          transition={{
            height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
          }}
          // `clip` (not `hidden`) so `overflow-clip-margin` can let the content panel's 1px
          // `shadow-card-rest` ring paint above this box — `hidden` shaved it against the header.
          style={{ overflow: "clip", overflowClipMargin: "1px" }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.18, ease: [0.22, 1, 0.36, 1] } }}
            className={cn("px-4 py-3 bg-base rounded-t-[12px]", className, "shadow-card-rest")}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})
ExpandableCardContent.displayName = "ExpandableCardContent"

export {
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
  useExpandableCardContext,
  useExpandableCardToggle,
}
